import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, useHistory, useLocation } from "react-router-dom";

import {
  CommunityLevelSection,
  sectionStore,
  NationalResourceEntityType,
  ChildReference,
  EolasFile,
  AnalyticsEvents,
  NationalResourcesClickPayload,
} from "@eolas-medical/core";

import { NavButton, PageTitle, InnerPageWrapper } from "UIKit";

import { compact, snakeCase } from "lodash";
import useSearchNationalResources from "modules/nationalResources/data/useSearchNationalResources";
import { EOLAS_CALCULATOR, CommunitySectionType } from "../types";
import { EolasList } from "Components/EolasFileList/EolasList";
import { useLocalSearch } from "shared/hooks/useLocalSearch";
import CommunityListItem from "Pages/Knowledge/components/KnowledgeSectionItem/KnowledgeSectionItem";
import { useSelectFile } from "Hooks";
import { trackEvent } from "API/Analytics";

const ClinicalCalculatorsBody: React.FC<RouteComponentProps> = observer(({ match: { url } }) => {
  const { onSelectFile } = useSelectFile();
  const [searchInput, setSearchInput] = useState("");
  const [searchItems, setSearchItems] = useState<any>([]);
  const [subSections, setSubSections] = useState<ChildReference[]>([]);
  const [showSearchResult, setShowSearchResult] = useState(false);

  const history = useHistory();

  const clinicalCalcsMainSectionID = sectionStore
    .getMainSectionList("community")
    .find(({ icon }) => icon === CommunityLevelSection.clinicalCalculators)?.id;

  const items = useMemo(() => {
    return subSections.map(({ id, name, description, icon }) => ({
      id,
      name: name || "",
      description: description || "Eolas Calculator",
      icon: icon || "",
    }));
  }, [subSections]);

  const location = useLocation();
  const { body } = (location.state as { body: string }) || {};

  const { t } = useTranslation();

  const { searchNationalResources, searchingNationalResources } = useSearchNationalResources();

  const { searchResult, onSetSearchInput } = useLocalSearch({
    data: searchItems,
    keysToSearch: ["name"],
  });

  const handleLocalSearch = useCallback(() => {
    setShowSearchResult(true);
    onSetSearchInput(searchInput);
  }, [onSetSearchInput, searchInput]);

  const handleClickCalculator = (calculator: EolasFile) => {
    const calculatorName = snakeCase(calculator.name as string);

    trackEvent(AnalyticsEvents.NATIONAL_RESOURCE_SELECTED, {
      nationalResourceType: NationalResourceEntityType.CALCULATOR,
      resourceName: calculator.name,
    } as NationalResourcesClickPayload);

    if (body === EOLAS_CALCULATOR) {
      history.push({ pathname: `eolas_calculator/${calculatorName}/${calculator.id}` });
    } else {
      onSelectFile(calculator);
    }
  };

  const handleClearSearch = useCallback(() => {
    setSearchInput("");
  }, []);

  useEffect(() => {
    if (body === EOLAS_CALCULATOR) {
      setSearchItems(items);
    } else {
      searchNationalResources(
        {
          entityType: NationalResourceEntityType.CALCULATOR,
          query: body,
          mode: "searchByNationalBody",
        },
        {
          onSuccess: (response) => {
            setSearchItems(response);
          },
        },
      );
    }
  }, [body, searchNationalResources, items]);

  useEffect(() => {
    const clinicalCalcsChildrenOrder = sectionStore.getSection(clinicalCalcsMainSectionID!)
      .childrenOrder;
    if (clinicalCalcsChildrenOrder && clinicalCalcsChildrenOrder.length) {
      setSubSections(compact(clinicalCalcsChildrenOrder));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchInput === "") {
      setShowSearchResult(false);
    }
  }, [searchInput]);

  return (
    <>
      <InnerPageWrapper>
        <PageTitle title={t("clinicalCalculators_title")} />
        <NavButton onClick={() => history.goBack()} />
        <EolasList<EolasFile, "click">
          value={searchInput}
          placeholderSearchText={t("findClinicalCalculator_searchPlaceholder")}
          items={showSearchResult ? searchResult : searchItems}
          isSearchable
          searchType="click"
          isLoading={searchingNationalResources}
          onSearchInputChange={setSearchInput}
          onClearSearch={handleClearSearch}
          onClickSearch={handleLocalSearch}
          renderItem={(eolasFile) => (
            <CommunityListItem
              eolasFile={eolasFile}
              onSelectFile={handleClickCalculator}
              sectionType={CommunitySectionType.NATIONAL_RESOURCE}
            />
          )}
        />
      </InnerPageWrapper>
    </>
  );
});

export default ClinicalCalculatorsBody;
